import {
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  inject,
  OnDestroy,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { Subscription, isObservable } from 'rxjs';

import { TranslateService } from './translate.service';
import { LangChangeEvent, TranslationChangeEvent } from './translate.store';
import { TRANSLATE_DEBUG_INDICATOR_PREFIX } from './translate-debug.constants';
import { TranslateDebugService } from './translate-debug.service';
import { TranslateDebugDialogService } from './translate-debug-dialog.service';
import { equals, isDefined } from './util';

@Pipe({
  name: 'translate',
  pure: false,
  standalone: true,
})
export class TranslatePipe implements PipeTransform, OnDestroy {
  private translate = inject(TranslateService);
  private _ref = inject(ChangeDetectorRef);
  private debugService = inject(TranslateDebugService);
  private debugDialogService = inject(TranslateDebugDialogService);
  private hostRef = inject(ElementRef, { optional: true });
  private renderer = inject(Renderer2, { optional: true });
  private readonly debugIndicatorPrefix = TRANSLATE_DEBUG_INDICATOR_PREFIX;

  value = '';
  lastKey: string | null = null;
  lastParams: any[] = [];
  lastInterpolateParams: any;
  onTranslationChange: Subscription | undefined;
  onLangChange: Subscription | undefined;
  onDefaultLangChange: Subscription | undefined;
  onDebugChange: Subscription | undefined;

  updateValue(key: string, interpolateParams?: any, translations?: any): void {
    const isDebug = this.debugService.isDebug$.value;
    const suppressIndicator = this.debugDialogService.shouldSuppressIndicatorForElement(
      this.hostRef?.nativeElement ?? null,
    );
    const applyIndicator = isDebug && !suppressIndicator;

    if (isDebug && key) {
      this.debugDialogService.registerDisplayedKey(key);
      this.applyDebugMetadata(key, true);
    } else {
      this.applyDebugMetadata(null, false);
    }

    const onTranslation = (res: any) => {
      const baseValue = res !== undefined ? res : key;
      const decoratedValue = this.decorateDebugValue(baseValue, applyIndicator);
      if (isDebug && key && typeof decoratedValue === 'string') {
        const baseString = typeof baseValue === 'string' ? baseValue : null;
        this.debugDialogService.registerDisplayedValue(key, baseString, decoratedValue);
      }
      if (this.value !== decoratedValue) {
        this.value = decoratedValue as string;
        this.lastKey = key;
        this._ref.markForCheck();
      }
    };

    if (translations) {
      const res = this.translate.getParsedResult(translations, key, interpolateParams);
      if (isObservable(res)) {
        res.subscribe(onTranslation);
      } else {
        onTranslation(res);
      }
    }

    this.translate.get(key, interpolateParams).subscribe(onTranslation);
  }

  private decorateDebugValue(value: any, applyIndicator: boolean): any {
    if (typeof value !== 'string') {
      return value;
    }

    const prefix = this.debugIndicatorPrefix;

    if (applyIndicator) {
      return value.startsWith(prefix) ? value : `${prefix}${value}`;
    }

    return value.startsWith(prefix) ? value.slice(prefix.length) : value;
  }

  private applyDebugMetadata(key: string | null, enable: boolean): void {
    if (!this.renderer) {
      return;
    }

    const host = this.resolveMetadataElement();
    if (!host) {
      return;
    }

    if (enable && key) {
      this.renderer.setAttribute(host, 'data-translate-key', key);
    } else {
      this.renderer.removeAttribute(host, 'data-translate-key');
    }
  }

  private resolveMetadataElement(): HTMLElement | null {
    const native = this.hostRef?.nativeElement;
    if (!native) {
      return null;
    }
    if (native instanceof HTMLElement) {
      return native;
    }
    if (native instanceof Comment || native instanceof Text) {
      return native.parentElement;
    }
    return null;
  }

  transform(query: string, ...args: any[]): any {
    if (!query || !query.length) {
      return query;
    }

    if (equals(query, this.lastKey) && equals(args, this.lastParams)) {
      return this.value;
    }

    let interpolateParams: any;

    if (isDefined(args[0]) && args.length) {
      if (typeof args[0] === 'string' && args[0].length) {
        const validArgs = args[0]
          .replace(/(\')?([a-zA-Z0-9_]+)(\')?(\s)?:/g, '"$2":')
          .replace(/:(\s)?(\')(.*?)(\')/g, ':"$3"');

        try {
          interpolateParams = JSON.parse(validArgs);
        } catch (e) {
          throw new SyntaxError(
            `Wrong parameter in TranslatePipe. Expected a valid Object, received: ${args[0]}`,
          );
        }
      } else if (typeof args[0] === 'object' && !Array.isArray(args[0])) {
        interpolateParams = args[0];
      }
    }

    this.lastKey = query;
    this.lastParams = args;
    this.lastInterpolateParams = interpolateParams;
    this.updateValue(query, interpolateParams);
    this._dispose();

    if (!this.onTranslationChange) {
      this.onTranslationChange = this.translate.onTranslationChange.subscribe(
        (event: TranslationChangeEvent) => {
          if (this.lastKey && event.lang === this.translate.currentLang) {
            this.lastKey = null;
            this.updateValue(query, interpolateParams, event.translations);
          }
        },
      );
    }

    if (!this.onLangChange) {
      this.onLangChange = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        if (this.lastKey) {
          this.lastKey = null;
          this.updateValue(query, interpolateParams, event.translations);
        }
      });
    }

    if (!this.onDefaultLangChange) {
      this.onDefaultLangChange = this.translate.onDefaultLangChange.subscribe(() => {
        if (this.lastKey) {
          this.lastKey = null;
          this.updateValue(query, interpolateParams);
        }
      });
    }

    if (!this.onDebugChange) {
      this.onDebugChange = this.debugService.isDebug$.subscribe(() => {
        if (this.lastKey) {
          this.updateValue(this.lastKey!, this.lastInterpolateParams);
        }
      });
    }

    return this.value;
  }

  private _dispose(): void {
    if (typeof this.onTranslationChange !== 'undefined') {
      this.onTranslationChange.unsubscribe();
      this.onTranslationChange = undefined;
    }
    if (typeof this.onLangChange !== 'undefined') {
      this.onLangChange.unsubscribe();
      this.onLangChange = undefined;
    }
    if (typeof this.onDefaultLangChange !== 'undefined') {
      this.onDefaultLangChange.unsubscribe();
      this.onDefaultLangChange = undefined;
    }
    if (typeof this.onDebugChange !== 'undefined') {
      this.onDebugChange.unsubscribe();
      this.onDebugChange = undefined;
    }
  }

  ngOnDestroy(): void {
    this._dispose();
  }
}
